export const FONT_FAMILIES = ['Basic', 'Inter', 'Tech', 'Monospace', 'Oswald', 'Georgia'];

// Font fallback configuration for graceful font loading
export const FONT_FALLBACKS = {
  Basic: ['Arial', 'Helvetica', 'sans-serif'],
  Inter: ['Arial', 'Helvetica', 'sans-serif'],
  Tech: ['Courier New', 'monospace'],
  Monospace: ['Courier New', 'monospace'],
  Oswald: ['Arial Narrow', 'Arial', 'sans-serif'],
  Georgia: ['Georgia', 'Times New Roman', 'serif'],
};

// Font configuration object
export const FONT_CONFIG = FONT_FAMILIES.map((fontName) => {
  const fontMapping = {
    Basic: 'Roboto, Arial, Helvetica, sans-serif',
    Inter: 'Inter, sans-serif',
    Tech: 'Courier New, monospace',
    Monospace: 'monospace',
    Oswald: 'Oswald, "Arial Narrow", Arial, sans-serif',
    Georgia: 'Georgia, serif',
  };

  return {
    name: fontName,
    family: fontMapping[fontName] || fontName,
  };
});

// Get font family with fallbacks
export const getFontWithFallbacks = (fontFamily) => {
  const fallbacks = FONT_FALLBACKS[fontFamily] || FONT_FALLBACKS['Basic'];
  return `"${fontFamily}", ${fallbacks.join(', ')}`;
};

export const COLORS = [
  '#FFFFFF',
  '#000000',
  '#FF0000',
  '#FF6600',
  '#FFFF00',
  '#00FF00',
  '#00FFFF',
  '#0066FF',
  '#6600FF',
  '#FF00FF',
  '#FF0066',
];

// Color names mapping for display
export const COLOR_NAMES = [
  'White',
  'Black',
  'Red',
  'Orange',
  'Yellow',
  'Green',
  'Cyan',
  'Blue',
  'Purple',
  'Magenta',
  'Pink',
];

// Color configuration object
export const COLOR_CONFIG = COLORS.map((color, index) => ({
  value: color,
  name: COLOR_NAMES[index] || `Color ${index + 1}`,
}));

// Enhanced dark futuristic theme color palette
export const DARK_THEME = {
  background: {
    primary: 'rgba(0, 20, 30, 0.95)',
    secondary: 'rgba(0, 10, 15, 0.95)',
    overlay: 'rgba(0, 20, 30, 0.9)',
    card: 'rgba(255, 255, 255, 0.05)',
    input: 'rgba(0, 20, 30, 0.95)',
    glass: 'rgba(0, 20, 30, 0.6)',
    neon: 'rgba(43, 255, 255, 0.05)',
  },
  accent: {
    primary: '#2BFFFF',
    secondary: '#1CA3A3',
    tertiary: '#00D4FF',
    glow: 'rgba(43, 255, 255, 0.4)',
    border: 'rgba(43, 255, 255, 0.3)',
    active: 'rgba(43, 255, 255, 0.2)',
    hover: 'rgba(43, 255, 255, 0.1)',
    pulse: 'rgba(43, 255, 255, 0.6)',
    shadow: 'rgba(43, 255, 255, 0.2)',
  },
  text: {
    primary: '#FFFFFF',
    secondary: 'rgba(255, 255, 255, 0.8)',
    muted: 'rgba(255, 255, 255, 0.6)',
    accent: '#2BFFFF',
    neon: '#2BFFFF',
    glow: 'rgba(43, 255, 255, 0.8)',
  },
  border: {
    primary: 'rgba(255, 255, 255, 0.2)',
    secondary: 'rgba(255, 255, 255, 0.1)',
    accent: 'rgba(43, 255, 255, 0.3)',
    active: '#2BFFFF',
    neon: 'rgba(43, 255, 255, 0.5)',
    glow: 'rgba(43, 255, 255, 0.4)',
  },
  state: {
    hover: 'rgba(43, 255, 255, 0.1)',
    active: 'rgba(43, 255, 255, 0.2)',
    focus: 'rgba(43, 255, 255, 0.3)',
    disabled: 'rgba(255, 255, 255, 0.1)',
    pressed: 'rgba(43, 255, 255, 0.3)',
  },
  effects: {
    glow: '0 0 20px rgba(43, 255, 255, 0.3)',
    glowStrong: '0 0 30px rgba(43, 255, 255, 0.5)',
    glowSubtle: '0 0 10px rgba(43, 255, 255, 0.2)',
    shadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    shadowStrong: '0 8px 25px rgba(0, 0, 0, 0.4)',
    neon: '0 0 5px #2BFFFF, 0 0 10px #2BFFFF, 0 0 15px #2BFFFF',
    pulse: '0 0 0 0 rgba(43, 255, 255, 0.4)',
  },
  gradients: {
    primary: 'linear-gradient(135deg, #2BFFFF 0%, #1CA3A3 100%)',
    secondary: 'linear-gradient(135deg, #00D4FF 0%, #2BFFFF 100%)',
    background: 'linear-gradient(180deg, rgba(0, 20, 30, 0.95) 0%, rgba(0, 10, 15, 0.95) 100%)',
    glass: 'linear-gradient(135deg, rgba(0, 20, 30, 0.8) 0%, rgba(0, 10, 15, 0.8) 100%)',
    neon: 'linear-gradient(45deg, #2BFFFF, #00D4FF, #2BFFFF)',
  },
  animations: {
    glow: `
      @keyframes glow {
        0%, 100% { box-shadow: 0 0 5px rgba(43, 255, 255, 0.2); }
        50% { box-shadow: 0 0 20px rgba(43, 255, 255, 0.4), 0 0 30px rgba(43, 255, 255, 0.2); }
      }
    `,
    pulse: `
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
    `,
    slideIn: `
      @keyframes slideIn {
        from { transform: translateY(-20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `,
    fadeIn: `
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `,
    neon: `
      @keyframes neon {
        0%, 100% { text-shadow: 0 0 5px #2BFFFF, 0 0 10px #2BFFFF, 0 0 15px #2BFFFF; }
        50% { text-shadow: 0 0 2px #2BFFFF, 0 0 5px #2BFFFF, 0 0 8px #2BFFFF; }
      }
    `,
    float: `
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
      }
    `,
  },
  transitions: {
    smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fast: 'all 0.2s ease',
    slow: 'all 0.5s ease',
    bounce: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    elastic: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  },
};

// Common styles for reusability with dark theme
export const COMMON_STYLES = {
  button: {
    base: {
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: 'none',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '500',
      padding: '6px 12px',
      outline: 'none',
      position: 'relative',
      // Dark theme hover and focus states
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: `0 4px 12px ${DARK_THEME.accent.glow}`,
      },
      '&:focus': {
        boxShadow: `0 0 0 2px ${DARK_THEME.accent.primary}`,
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },
    primary: {
      background: `linear-gradient(135deg, ${DARK_THEME.accent.primary} 0%, ${DARK_THEME.accent.secondary} 100%)`,
      border: `2px solid ${DARK_THEME.accent.primary}`,
      color: DARK_THEME.text.primary,
      boxShadow: `0 0 15px ${DARK_THEME.accent.glow}`,
      '&:hover': {
        background: `linear-gradient(135deg, ${DARK_THEME.accent.primary} 0%, ${DARK_THEME.accent.secondary} 100%)`,
        boxShadow: `0 0 20px ${DARK_THEME.accent.glow}, 0 4px 12px rgba(0, 0, 0, 0.3)`,
        transform: 'translateY(-2px)',
      },
      '&:focus': {
        boxShadow: `0 0 0 3px ${DARK_THEME.accent.focus}, 0 0 15px ${DARK_THEME.accent.glow}`,
      },
    },
    secondary: {
      background: DARK_THEME.background.card,
      border: `1px solid ${DARK_THEME.border.primary}`,
      color: DARK_THEME.text.primary,
      '&:hover': {
        background: DARK_THEME.state.hover,
        border: `1px solid ${DARK_THEME.border.accent}`,
        color: DARK_THEME.text.accent,
      },
      '&:focus': {
        border: `1px solid ${DARK_THEME.accent.primary}`,
        boxShadow: `0 0 0 2px ${DARK_THEME.accent.focus}`,
      },
    },
    danger: {
      background: 'rgba(255, 68, 68, 0.2)',
      border: '1px solid rgba(255, 68, 68, 0.5)',
      color: '#FF4444',
      '&:hover': {
        background: 'rgba(255, 68, 68, 0.3)',
        border: '1px solid #FF4444',
        boxShadow: '0 0 12px rgba(255, 68, 68, 0.4)',
      },
      '&:focus': {
        boxShadow: '0 0 0 2px rgba(255, 68, 68, 0.5)',
      },
    },
    close: {
      position: 'absolute',
      top: '-10px',
      right: '-10px',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: '#FF4444',
      border: '2px solid #FFFFFF',
      color: 'white',
      fontSize: '16px',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        background: '#FF3333',
        transform: 'scale(1.05)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      },
      '&:focus': {
        outline: 'none',
        boxShadow: '0 0 0 2px #FF4444',
      },
    },
  },
  input: {
    container: {
      background: 'transparent',
      border: '1px solid #FFFFFF',
      borderRadius: '0',
      padding: '12px 16px',
      boxShadow: 'none',
      cursor: 'text',
      transition: 'all 0.2s ease',
      position: 'relative',
      minWidth: '300px',
      minHeight: '50px',
      '&:hover': {
        border: '1px solid rgba(255, 255, 255, 0.8)',
      },
      '&:focusWithin': {
        border: '2px solid #FFFFFF',
      },
    },
    field: {
      width: '100%',
      minWidth: '250px',
      maxWidth: '90vw',
      padding: '0',
      border: 'none',
      borderRadius: '0',
      background: 'transparent',
      color: '#FFFFFF',
      fontSize: '16px',
      outline: 'none',
      textAlign: 'left',
      fontWeight: '400',
      WebkitAppearance: 'none',
      boxSizing: 'border-box',
      resize: 'none',
      fontFamily: 'inherit',
      lineHeight: '1.4',
      '&::placeholder': {
        color: '#FFFFFF',
        opacity: '0.7',
      },
      '&:focus': {
        color: '#FFFFFF',
      },
    },
  },
  container: {
    borderBottom: `1px solid ${DARK_THEME.border.accent}`,
    padding: '16px',
    background: DARK_THEME.background.primary,
  },
};

// Utility function to merge styles
export const mergeStyles = (...styleObjects) => Object.assign({}, ...styleObjects);

// Layout configuration for optimized TextEditor
export const LAYOUT_CONFIG = {
  TOTAL_HEIGHT: 130, // EXACT height constraint
  FONT_ROW_HEIGHT: 28, // Row 1: Font selector
  SCALE_ROW_HEIGHT: 22, // Row 2: Scale slider
  ROTATE_ROW_HEIGHT: 22, // Row 3: Rotate slider
  COLOR_ROW_HEIGHT: 22, // Row 4: Color palette
  PADDING: 10, // Top/bottom padding
  SIDE_PADDING: 12, // Left/right padding
  GAP: 6, // Row gap between rows
  // Column widths for sliders - updated to match new design
  LABEL_WIDTH: 60, // Fixed width for labels (was 56)
  VALUE_WIDTH: 40, // Fixed width for values (was 36)
  // Responsive breakpoints
  MOBILE_BREAKPOINT: 768,
  TABLET_BREAKPOINT: 1024,
  MIN_WIDTH: 320,
  // Touch targets
  MIN_TOUCH_TARGET: 20,
  PREFERRED_TOUCH_TARGET: 28,
  // Mobile optimizations - Same as desktop for consistency
  MOBILE_FONT_ROW_HEIGHT: 28,
  MOBILE_SCALE_ROW_HEIGHT: 22,
  MOBILE_ROTATE_ROW_HEIGHT: 22,
  MOBILE_COLOR_ROW_HEIGHT: 20,
  MOBILE_PADDING: 10,
  MOBILE_SIDE_PADDING: 12,
  MOBILE_GAP: 6,
  // Legacy support (will be deprecated)
  SLIDER_ROW_HEIGHT: 50, // Combined height for both sliders (22 + 6 + 22)
  MOBILE_SLIDER_ROW_HEIGHT: 50, // Combined height for both sliders
  // Error handling and validation
  MAX_HEIGHT_TOLERANCE: 0, // No tolerance - must be exactly 130px
  OVERFLOW_PROTECTION: true, // Enable overflow protection
};

// CSS Grid container styles for optimized TextEditor
export const TEXT_EDITOR_GRID_STYLES = {
  container: {
    height: `${LAYOUT_CONFIG.TOTAL_HEIGHT}px`,
    display: 'grid',
    gridTemplateRows: `${LAYOUT_CONFIG.FONT_ROW_HEIGHT}px ${LAYOUT_CONFIG.SLIDER_ROW_HEIGHT}px ${LAYOUT_CONFIG.COLOR_ROW_HEIGHT}px`,
    gap: `${LAYOUT_CONFIG.GAP}px`,
    padding: `${LAYOUT_CONFIG.PADDING}px`,
    background: 'rgba(0, 20, 30, 0.95)',
    borderRadius: '12px',
    border: '1px solid rgba(43, 255, 255, 0.3)',
    boxSizing: 'border-box',
    overflow: 'hidden', // Prevent height overflow
  },
  fontRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none', // Firefox
    msOverflowStyle: 'none', // IE/Edge
    '&::-webkit-scrollbar': {
      display: 'none', // Chrome/Safari
    },
  },
  sliderRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    alignItems: 'center',
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
};

// Responsive calculations
export const getResponsivePadding = (screenWidth) => {
  if (screenWidth < LAYOUT_CONFIG.MOBILE_BREAKPOINT) {
    return LAYOUT_CONFIG.MOBILE_PADDING;
  }
  return LAYOUT_CONFIG.PADDING;
};

export const getResponsiveGap = (screenWidth) => {
  if (screenWidth < LAYOUT_CONFIG.MOBILE_BREAKPOINT) {
    return LAYOUT_CONFIG.MOBILE_GAP;
  }
  return LAYOUT_CONFIG.GAP;
};

// Get responsive container styles based on screen width with dark theme and error handling
// getResponsiveContainerStyles was removed as it was unused.

// Touch-friendly sizing utilities
export const getTouchFriendlySize = (baseSize, isMobile = false) => {
  if (isMobile) {
    return Math.max(baseSize, LAYOUT_CONFIG.MIN_TOUCH_TARGET);
  }
  return baseSize;
};

// Media query helpers
export const MEDIA_QUERIES = {
  mobile: `@media (max-width: ${LAYOUT_CONFIG.MOBILE_BREAKPOINT - 1}px)`,
  tablet: `@media (min-width: ${LAYOUT_CONFIG.MOBILE_BREAKPOINT}px) and (max-width: ${
    LAYOUT_CONFIG.TABLET_BREAKPOINT - 1
  }px)`,
  desktop: `@media (min-width: ${LAYOUT_CONFIG.TABLET_BREAKPOINT}px)`,
  touchDevice: '@media (hover: none) and (pointer: coarse)',
};

// Color swatch styles for exact 20px row height
// getResponsiveColorSwatchStyles was removed as it was unused.

// Common container styles
export const createContainerStyle = (additionalStyles = {}) => ({
  ...COMMON_STYLES.container,
  ...additionalStyles,
});

// Comprehensive dark theme CSS styles for all interactive elements
export const DARK_THEME_CSS = `
  /* Global dark theme styles */
  /* Button hover and focus states */
  .clickable:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${DARK_THEME.accent.glow};
  }

  .clickable:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${DARK_THEME.accent.primary};
  }

  /* Font button states - clean flat design */
  .font-button:hover {
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    background: rgba(255, 255, 255, 0.08) !important;
    color: #FFFFFF !important;
    transform: none;
  }

  .font-button:focus {
    outline: none;
    border: 1px solid rgba(255, 255, 255, 0.6) !important;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  .font-button.active {
    border: 1px solid #FFFFFF !important;
    background: rgba(255, 255, 255, 0.1) !important;
    color: #FFFFFF !important;
    box-shadow: none;
  }

  .font-button.active:hover {
    background: rgba(255, 255, 255, 0.15) !important;
    box-shadow: none;
    transform: none;
  }

  /* Color swatch states - clean flat design */
  .color-swatch:hover {
    border: 1px solid rgba(255, 255, 255, 0.4) !important;
    transform: none;
    box-shadow: none;
  }

  .color-swatch:focus {
    outline: none;
    border: 1px solid rgba(255, 255, 255, 0.6) !important;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }

  .color-swatch.selected {
    border: 2px solid #FFFFFF !important;
    box-shadow: none;
    transform: none;
  }

  .color-swatch.selected:hover {
    transform: none;
    box-shadow: none;
  }

  /* MUI Slider clean flat design overrides */
  .MuiSlider-root {
    color: #FFFFFF !important;
    position: static !important; /* Ensure no absolute positioning */
    overflow: visible !important; /* Allow slider to render properly */
  }

  .MuiSlider-track {
    background-color: #FFFFFF !important;
    border: none !important;
    top: 7px;
    position: relative !important;
  }

  .MuiSlider-rail {
    background-color: rgba(255, 255, 255, 0.2) !important;
    position: relative !important;
  }

  .MuiSlider-thumb {
    background-color: #FFFFFF !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    position: relative !important;
    top: 7px;
  }

  .MuiSlider-thumb:hover {
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.1) !important;
  }

  .MuiSlider-thumb:focus,
  .MuiSlider-thumb.Mui-focusVisible {
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.2) !important;
  }

  .MuiSlider-thumb:active {
    box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.15) !important;
  }

  .MuiSlider-valueLabel {
    background-color: ${DARK_THEME.background.overlay} !important;
    color: ${DARK_THEME.text.accent} !important;
    position: static !important;
  }

  /* Input field states */
  .text-input:focus {
    outline: none;
    box-shadow: none;
  }

  .text-input::placeholder {
    color: ${DARK_THEME.text.muted} !important;
  }

  /* Text element list styling */
  .text-element-item {
    background: ${DARK_THEME.background.card};
    border: 1px solid ${DARK_THEME.border.primary};
    transition: all 0.2s ease;
  }

  .text-element-item:hover {
    background: ${DARK_THEME.state.hover};
    border: 1px solid ${DARK_THEME.border.accent};
  }

  .text-element-item.active {
    border: 2px solid ${DARK_THEME.accent.primary} !important;
    background: ${DARK_THEME.state.active};
  }

  .text-element-item.active:hover {
    background: ${DARK_THEME.state.focus};
  }

  /* Touch device optimizations */
  @media (hover: none) and (pointer: coarse) {
    .font-button:hover,
    .color-swatch:hover,
    .clickable:hover {
      transform: none;
    }
    
    .font-button:active {
      transform: scale(0.95);
      background: ${DARK_THEME.state.focus} !important;
    }
    
    .color-swatch:active {
      transform: scale(0.95);
      filter: brightness(1.2);
    }
    
    .clickable:active {
      transform: scale(0.98);
    }
  }

  /* Accessibility improvements */
  @media (prefers-reduced-motion: reduce) {
    * {
      transition: none !important;
      animation: none !important;
    }
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    .font-button,
    .color-swatch,
    .clickable {
      border-width: 2px !important;
    }
    
    .font-button.active,
    .color-swatch.selected {
      border-width: 3px !important;
    }
  }
`;
